/**
 * Realm Context Provider — Backend-Resolved
 *
 * Resolves the current realm by calling the backend's
 * GET /api/v1/realm/resolve?domain={hostname} endpoint.
 *
 * The backend (via REALM_FRONTEND_DOMAINS) is the single source of truth
 * for domain-to-realm mappings, eliminating config.json duplication.
 */

import {
  createContext,
  useContext,
  useState,
  useEffect,
  type ReactNode,
} from "react";
import { getConfigSync } from "../config";

interface RealmContextValue {
  realmId: string;
  hostname: string;
  error: string | null;
  isLoading: boolean;
}

const RealmContext = createContext<RealmContextValue | null>(null);

interface RealmProviderProps {
  children: ReactNode;
}

export function RealmProvider({ children }: RealmProviderProps) {
  const [value, setValue] = useState<RealmContextValue>({
    realmId: "",
    hostname: typeof window !== "undefined" ? window.location.hostname : "",
    error: null,
    isLoading: true,
  });

  useEffect(() => {
    const hostname =
      typeof window !== "undefined" ? window.location.hostname : "";

    if (!hostname) {
      setValue({
        realmId: "",
        hostname,
        error: "Cannot determine hostname",
        isLoading: false,
      });
      return;
    }

    const config = getConfigSync();
    const apiBase = import.meta.env.DEV ? "" : config.apiBaseUrl;

    fetch(
      `${apiBase}/api/v1/realm/resolve?domain=${encodeURIComponent(hostname)}`
    )
      .then(async (res) => {
        if (!res.ok) {
          const body = await res.json().catch(() => null);
          const msg =
            body?.detail?.message ||
            `No realm configured for domain: ${hostname}`;
          setValue({
            realmId: "",
            hostname,
            error: msg,
            isLoading: false,
          });
          return;
        }
        const data = await res.json();
        if (import.meta.env.DEV) {
          console.log(
            `[RealmContext] Resolved realm: ${data.realm_id} from hostname: ${hostname}`
          );
        }
        setValue({
          realmId: data.realm_id,
          hostname,
          error: null,
          isLoading: false,
        });
      })
      .catch((err) => {
        console.error("[RealmContext] Failed to resolve realm:", err);
        setValue({
          realmId: "",
          hostname,
          error: "Failed to resolve realm — backend may be unavailable",
          isLoading: false,
        });
      });
  }, []);

  return (
    <RealmContext.Provider value={value}>{children}</RealmContext.Provider>
  );
}

export function useRealm(): RealmContextValue {
  const context = useContext(RealmContext);
  if (!context) {
    throw new Error("useRealm must be used within a RealmProvider");
  }
  return context;
}

export default RealmContext;
